/**
 * UnifiedChartTab - Single tab chart component adapted from e80c574d architecture
 * 
 * Combines chart + details in unified component using the proven architecture
 * from commit e80c574d with multi-tab support
 */

import { devLog, devWarn, devError } from '../../../utils/devLog';
import React, { useEffect, forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { ParsedChartData } from '../../../models/types';
import { SharedChartData, SharedViewMode } from '../../../types/share.types';
import ViewportContainer from './ViewportContainer';
import ChartDetails from './ChartDetails';
import PlanetDragOverlay from './PlanetDragOverlay';
import TimeMotionOverlay from './TimeMotionOverlay';
import { CHART_CONSTANTS } from './constants';
import { chartColorPresetManager } from '../../../config/chartColorPresets';
import { useTabChart } from './universal_architecture/simplified/useTabChart';
import { usePlanetDragInteraction } from '../../../hooks/usePlanetDragInteraction';
import { useTimeMotionState } from '../../../hooks/useTimeMotionState';
import { globalEventStream } from './universal_architecture/core/EventStream';
// useMultipleCharts removed - settings passed via props to avoid context re-renders

import './AstrologyChart.css';

// =============================================================================
// Helper: Parse timezone string "UTC+02:00" or "UTC-05:30" → number
// =============================================================================
function parseTimezoneOffset(timezone: string | undefined): number {
    if (!timezone) return 0;

    // Match pattern "UTC+02:00" or "UTC-05:30"
    const match = timezone.match(/^UTC([+-])(\d{2}):(\d{2})$/);
    if (!match) {
        devWarn('[PlanetDrag] Failed to parse timezone:', timezone);
        return 0;
    }

    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);

    return sign * (hours + minutes / 60);
}

// =============================================================================
// Helper: Parse date + time strings to Date object (as local time of chart location)
// =============================================================================
function parseChartDateTime(date: string | undefined, time: string | undefined): Date {
    if (!date || !time) return new Date();

    try {
        // Date format: "December 26, 2025"
        // Time format: "14:49:00"
        // Parse using UTC to avoid browser timezone interference
        const months: Record<string, number> = {
            'January': 0, 'February': 1, 'March': 2, 'April': 3,
            'May': 4, 'June': 5, 'July': 6, 'August': 7,
            'September': 8, 'October': 9, 'November': 10, 'December': 11
        };

        const dateMatch = date.match(/^(\w+)\s+(\d+),\s+(\d+)$/);
        const timeMatch = time.match(/^(\d+):(\d+):(\d+)$/);

        if (!dateMatch || !timeMatch) {
            devWarn('[PlanetDrag] Failed to parse date/time:', date, time);
            return new Date();
        }

        const month = months[dateMatch[1]];
        const day = parseInt(dateMatch[2], 10);
        const year = parseInt(dateMatch[3], 10);
        const hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        const seconds = parseInt(timeMatch[3], 10);

        // Create Date using UTC to preserve exact time values
        // Backend will handle timezone based on coordinates
        return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    } catch (e) {
        devWarn('[PlanetDrag] Error parsing date:', e);
        return new Date();
    }
}

export interface UnifiedChartTabProps {
    // Tab-specific props
    chartId: string;
    chartData: ParsedChartData;
    isActive: boolean;

    // Chart configuration props
    width?: number | string;
    height?: number | string;
    displayMode?: 'symbols' | 'short';
    enableAnimations?: boolean;
    showDetails?: boolean;
    colorPreset?: string;

    // Share mode props
    shareMode?: boolean;  // If true, pass to ChartDetails for read-only (all sections always visible)
    sharedViewMode?: SharedViewMode;  // View mode for shared charts (author/guest)
    sharedChartData?: SharedChartData;  // Shared chart data with share_config

    // Callbacks
    onZoomChange?: (zoomLevel: number) => void;
    onError?: (error: string) => void;

    // Debug
    debugMode?: boolean;
    className?: string;

    // Chart settings (passed from parent to avoid context re-renders)
    chartSettings?: {
        enableAnimations?: boolean;
        colorPreset?: string;
    };
}

export interface UnifiedChartTabRef {
    getStats: () => any;
    forceRender: (chartData: ParsedChartData) => Promise<void>;
    switchDisplayMode: (mode: 'symbols' | 'short') => Promise<void>;
    cleanup: () => void;
    refresh: () => void;
}

/**
 * Unified chart tab component - combines SVG chart + details panel
 * Based on proven architecture from commit e80c574d
 */
const UnifiedChartTabComponent: React.ForwardRefRenderFunction<
    UnifiedChartTabRef,
    UnifiedChartTabProps
> = ({
    chartId,
    chartData,
    isActive,
    width = '100%',
    height = CHART_CONSTANTS.CHART_DIMENSIONS.DEFAULT_HEIGHT,
    displayMode = 'short',
    enableAnimations = false,
    showDetails = true,
    colorPreset = 'default',
    shareMode = false,
    sharedViewMode,
    sharedChartData,
    onZoomChange,
    onError,
    debugMode = false,
    className = '',
    chartSettings
}, ref) => {
    // Get settings from props (passed from parent to avoid context re-renders)
    const actualEnableAnimations = chartSettings?.enableAnimations ?? enableAnimations;
    const actualColorPreset = chartSettings?.colorPreset ?? colorPreset;
    
    // Apply color preset on mount and when it changes
    useEffect(() => {
        if (actualColorPreset) {
            chartColorPresetManager.applyPreset(actualColorPreset);
        } else {
            chartColorPresetManager.applyDefaultPreset();
        }
    }, [actualColorPreset]);
    
    // Tab chart system using adapted hook
    const chart = useTabChart({
        chartId,
        debugMode,
        enableAnimations: actualEnableAnimations,
        initialDisplayMode: displayMode
    });

    // Planet drag interaction (only for active charts, not in share mode)
    // Get location and datetime from apiResponse.basic_info
    const basicInfo = chartData?.apiResponse?.basic_info;
    const timezoneOffset = parseTimezoneOffset(basicInfo?.timezone);
    const chartDateTime = parseChartDateTime(basicInfo?.date, basicInfo?.time);

    // Debug: log chart data used for drag, including Lagna position from API
    const grahaPositions = chartData?.apiResponse?.graha_positions?.planets;
    const lagnaFromChart = grahaPositions?.find((p: { short_name: string }) => p.short_name === 'Lg');
    // Parse longitude from position string: "rashi|degreesInRashi|longitude|speed"
    const lagnaLongitude = lagnaFromChart?.position?.split('|')[2];
    devLog(`[PlanetDrag:UnifiedChartTab] Chart Lagna: ${lagnaLongitude}° (from apiResponse), time: ${basicInfo?.time}`);

    usePlanetDragInteraction(
        chartId,
        {
            lat: basicInfo?.latitudeValue ?? 0,
            lon: basicInfo?.longitudeValue ?? 0,
            timezoneOffset,
            chartDateTime,
        }
    );

    // Time motion state handler
    useTimeMotionState({
        chartId,
        latitude: basicInfo?.latitudeValue ?? 0,
        longitude: basicInfo?.longitudeValue ?? 0,
        timezoneOffset,
        currentTime: chartDateTime,
    });

    // Responsive panel sizes and direction based on viewport
    const [chartPanelSize, setChartPanelSize] = useState(50);
    const [panelDirection, setPanelDirection] = useState<'horizontal' | 'vertical'>('horizontal');

    useEffect(() => {
        const calculatePanelSettings = () => {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            if (viewportWidth < viewportHeight) {
                // Portrait: vertical layout (width < height)
                setPanelDirection('vertical');
                setChartPanelSize(50);
            } else {
                // Landscape: horizontal layout (width >= height)
                setPanelDirection('horizontal');
                setChartPanelSize(50);
            }
        };

        calculatePanelSettings();
        window.addEventListener('resize', calculatePanelSettings);

        return () => window.removeEventListener('resize', calculatePanelSettings);
    }, []);

    // Note: chart.data.updated events are now emitted ONLY from MultipleChartsContext
    // (loadChart, loadChartFromData, navigateHistory, addCurrentTimeSnapshot, chartDataUpdater)
    // This ensures single source of truth with proper snapshotId in every event

    // SVG ready callback - emit event when SVG is mounted in DOM
    const handleSvgRef = useCallback((svgElement: SVGSVGElement | null) => {
        if (svgElement) {
            // SVG is now mounted in DOM - emit ready event
            globalEventStream.emit({
                id: `svg-ready-${chartId}-${Date.now()}`,
                type: 'svg.ready',
                source: `unified-chart-tab-${chartId}`,
                chartId: chartId,
                timestamp: Date.now(),
                data: {
                    svgSelector: `svg.chart-svg-${chartId}`,
                    chartId: chartId
                }
            });
        }
    }, [chartId]);

    // Ref API for external control
    useImperativeHandle(ref, () => ({
        getStats: () => chart.getStats(),
        forceRender: async (parsedChartData: ParsedChartData) => {
            if (parsedChartData.apiResponse) {
                await chart.forceRender(parsedChartData.apiResponse);
            } else {
                throw new Error('No apiResponse in ParsedChartData');
            }
        },
        switchDisplayMode: chart.switchDisplayMode,
        cleanup: chart.cleanup,
        refresh: () => {
            if (chartData?.apiResponse) {
                chart.forceRender(chartData.apiResponse).catch(console.error);
            }
        }
    }), [chart, chartData]);
    
    // Zoom handling with chart ID namespace
    useEffect(() => {
        if (onZoomChange && isActive) {
            const subscription = globalEventStream.subscribe(`ZOOM_CHANGED_${chartId}`, (event: any) => {
                onZoomChange(event.zoomLevel || 1.0);
            });

            return () => subscription?.unsubscribe?.();
        }
    }, [onZoomChange, isActive, chartId]);
    
    // LAZY LOADING: Trigger initial render when chart system is ready
    // This is event-driven: component signals readiness, then requests data render
    // NOTE: Only trigger on isInitialized change, NOT on apiResponse/chartData change
    // apiResponse changes from metadata edits should NOT trigger re-render
    const hasTriggeredInitialRender = React.useRef(false);
    useEffect(() => {
        if (chart.isInitialized && chartData?.apiResponse && !hasTriggeredInitialRender.current) {
            hasTriggeredInitialRender.current = true;
            devLog(`[UnifiedChartTab] 🚀 Chart system ready, triggering initial render for ${chartId}`);

            // Emit chart.data.updated to trigger render (TabChartEventManager is now subscribed)
            globalEventStream.emit({
                id: `chart-data-updated-init-${chartId}-${Date.now()}`,
                type: `chart.data.updated.${chartId}`,
                source: `unified-chart-tab-init-${chartId}`,
                chartId,
                snapshotId: `snapshot-original-${chartId}`,
                apiResponse: chartData.apiResponse,
                timestamp: Date.now()
            });
        }
    }, [chart.isInitialized, chartId]); // Removed chartData dependency - only trigger on init

    // DOM ready notification with chart ID (no timeout - emit immediately)
    useEffect(() => {
        if (chart.isInitialized) {
            globalEventStream.emit({
                id: `dom-ready-${chartId}-${Date.now()}`,
                type: `dom.ready.${chartId}`,
                source: `unified-chart-tab-${chartId}`,
                chartId,
                isActiveTab: isActive,
                timestamp: Date.now()
            });
        }
    }, [chart.isInitialized, chartId, isActive]);
    
    // Debug info
    useEffect(() => {
        if (debugMode && chart.isInitialized) {
            
            // Add to window for debugging with chart ID
            if (typeof window !== 'undefined') {
                if (!window.chartTabs) {
                    window.chartTabs = {};
                }
                window.chartTabs[chartId] = chart;
            }
        }
    }, [chart.isInitialized, chart, chartId, debugMode]);
    
    // Error logging
    useEffect(() => {
        if (chart.error) {
            devError(`❌ UnifiedChartTab[${chartId}]: Error:`, chart.error);
            onError?.(chart.error);
        }
    }, [chart.error, chartId, onError]);
    
    // CSS classes for tab state
    const containerClasses = [
        'unified-chart-tab',
        'astrology-chart-wrapper',
        isActive ? 'active' : 'inactive',
        className
    ].filter(Boolean).join(' ');

    return (
        <div
            className={containerClasses}
            data-chart-id={chartId}
            style={{
                width,
                height,
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: 'var(--chart-details-bg, #f8f9fa)',
                overflow: 'hidden'
                // Видимость управляется только через ChartLayerManager
            }}
        >
            <PanelGroup direction={panelDirection}>
                {/* Chart Panel - resizable */}
                <Panel
                    defaultSize={chartPanelSize}
                    minSize={0}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                >
                    {/* Planet drag overlay - shows loading/time during drag */}
                    {!shareMode && <PlanetDragOverlay chartId={chartId} />}

                    {/* Time motion overlay - shows loading/time during time navigation */}
                    {!shareMode && <TimeMotionOverlay chartId={chartId} />}

                    {/* Main chart SVG - scales with panel */}
                    <svg
                        ref={handleSvgRef}
                        id={`chart-svg-${chartId}`}
                        className={`astrology-chart-svg chart-svg-${chartId}`}
                        viewBox="0 0 700 700"
                        preserveAspectRatio="xMidYMid meet"
                        style={{
                            width: '100%',
                            flex: 1,
                            minHeight: 0
                        }}
                    >
                        <ViewportContainer
                            width={700}
                            height={700}
                            viewportId={`viewport-${chartId}`}
                            id={`viewport-content-${chartId}`}
                            className={`viewport-content viewport-content-${chartId} main-viewport`}
                        >
                            {/* Chart content will be injected here by the engines */}
                        </ViewportContainer>
                    </svg>
                </Panel>

                {/* Resize Handle - wider on touch devices */}
                <PanelResizeHandle
                    className="panel-resize-handle"
                    style={{
                        width: panelDirection === 'horizontal' ? '6px' : '100%',
                        height: panelDirection === 'horizontal' ? '100%' : '6px',
                        backgroundColor: 'var(--chart-border-default, #ddd)',
                        cursor: panelDirection === 'horizontal' ? 'col-resize' : 'row-resize',
                        position: 'relative',
                        transition: 'background-color 0.2s',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        touchAction: 'none'
                    }}
                    onMouseEnter={(e) => {
                        const target = e.currentTarget as unknown as HTMLElement;
                        if (target && target.style) {
                            target.style.backgroundColor = '#ffeee5';
                        }
                    }}
                    onMouseLeave={(e) => {
                        const target = e.currentTarget as unknown as HTMLElement;
                        if (target && target.style) {
                            target.style.backgroundColor = 'var(--chart-border-default, #ddd)';
                        }
                    }}
                >
                    {/* Visual grip indicator */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: panelDirection === 'horizontal' ? 'column' : 'row',
                            gap: '3px',
                            opacity: 0.6
                        }}
                    >
                        <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#888' }} />
                        <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#888' }} />
                        <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#888' }} />
                    </div>
                </PanelResizeHandle>

                {/* Details Panel */}
                {showDetails && (
                    <Panel
                        minSize={0}
                        style={{
                            overflow: 'hidden'
                        }}
                    >
                        <div
                            className={`chart-info-panel chart-info-${chartId}`}
                            style={{
                                borderLeft: panelDirection === 'horizontal' ? '1px solid var(--chart-border-default, #ddd)' : 'none',
                                borderTop: panelDirection === 'vertical' ? '1px solid var(--chart-border-default, #ddd)' : 'none'
                            }}
                        >
                            <ChartDetails
                                chartId={chartId}
                                chartData={chartData}
                                shareMode={shareMode}
                                sharedViewMode={sharedViewMode}
                                sharedChartData={sharedChartData}
                            />
                        </div>
                    </Panel>
                )}
            </PanelGroup>

            {/* Error display */}
            {chart.error && (
                <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '10px',
                    background: 'var(--planet-lagna-color, #dc3545)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    maxWidth: '300px',
                    zIndex: 1000
                }}>
                    Error: {chart.error}
                </div>
            )}

            {/* Performance info in dev mode */}
            {debugMode && chart.lastRenderTime > 0 && (
                <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    zIndex: 1000
                }}>
                    Tab: {chartId} | Render: {chart.lastRenderTime}ms
                </div>
            )}
            
            {/* Chart ID indicator in debug mode */}
            {debugMode && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    zIndex: 1000
                }}>
                    ID: {chartId} | {isActive ? 'ACTIVE' : 'INACTIVE'}
                </div>
            )}
        </div>
    );
};

// Wrap with memo to prevent re-renders when switching tabs (CSS handles visibility)
const UnifiedChartTab = React.memo(
  forwardRef(UnifiedChartTabComponent),
  (prevProps, nextProps) => {
    // Only re-render if these props actually change
    // isActive is always false (CSS handles visibility), so we skip it
    const shouldSkipRerender = (
      prevProps.chartId === nextProps.chartId &&
      prevProps.chartData === nextProps.chartData &&
      prevProps.showDetails === nextProps.showDetails &&
      prevProps.shareMode === nextProps.shareMode &&
      prevProps.sharedViewMode === nextProps.sharedViewMode &&
      prevProps.debugMode === nextProps.debugMode &&
      prevProps.chartSettings === nextProps.chartSettings
    );

    return shouldSkipRerender;
  }
);

export default UnifiedChartTab;